import { ServerConfig } from "@/config/config";
import { Purchase, Sale } from "@/models/base";
import { Job } from "@/models/job";
import { BankService } from "@/services/bank-service";
import { DossierConfigService } from "@/services/dossier-config-service";
import { Axios } from "@/utils/axios";
import { downloadBlob, downloadUrl } from "@/utils/blob";
import { HttpError } from "@/utils/services/http-error";
import { isStringNullOrWhitespace } from "@/utils/strings";
import axios from "axios";
import Vue from "vue";
import { UblS3UploadedFile } from "../models/ubl-archive-file";

export interface IPublicUblFile {
	fileInfo:UblS3UploadedFile;
	userName:string;
	companyName:string;
}

export type UblPreCreateWarning = "default-bank-missing-iban-or-bic"


export interface IUblPurchaseWrapper {
	Purchase:any;
	Attachments:{FileName:string, MimeType:string, FileContent:string}[];
}

export interface ICreateUBLArchiveOptions {
	journalIds?:number[];
	saleIds?:number[];
	startDate?:Date;
	endDate?:Date;
	message?:string;
	typeIds?:number[];
}

export interface ICreatePurchaseUBLArchiveOptions {
	beginDate?:Date;
	endDate?:Date;
	message?:string;
	vatPeriod?:number;
	journalIds?:number[];
	purchaseIds?:number[];
}

class UblServiceClass {
	public readonly publicUrl:string = `${ServerConfig.host}/ubl`
	public readonly url:string = `${this.publicUrl}/private`;

	public getPreCreateUblWarnings():UblPreCreateWarning[] {
		let warnings:UblPreCreateWarning[] = [];
		if (!this.hasValidBankConfigured()) {
			warnings.push("default-bank-missing-iban-or-bic");
		}
		return warnings;
	}

	private hasValidBankConfigured():boolean {
		let bank = BankService.getType(DossierConfigService.getGeneralConfig().DefaultBankTypeID);
		if (!bank) return false;
		if (isStringNullOrWhitespace(bank.Bic)) return false;
		if (isStringNullOrWhitespace(bank.Iban)) return false;
		return true;
	}

	public async showWarnings(vue:Vue):Promise<void> {
		let warnings = this.getPreCreateUblWarnings();
		let yesText = vue.$ct("common.yes");
		let noText = vue.$ct("common.no");
		for (let warning of warnings) {
			let shouldContinue = await vue.$wf.confirm(vue.$ct(`components.common-ubl.warnings.${warning}`), yesText, noText, vue.$ct("common.warning"), "warning");
			if (!shouldContinue) {
				let err = new HttpError("Action canceled", 0);
				err.dontShow = true;
				throw err;
			}
		}
	}

	public async createInvoiceUbl(vue:Vue, sale:Sale, options:{disableWarnings?:boolean} = {}):Promise<string>{
		if (!options.disableWarnings){
			await this.showWarnings(vue);
		}
		let result = await Axios.post(`${this.url}/download-ubl/${sale.ID}`);
		return result.data;
	}

	public async downloadInvoiceUbl(vue:Vue, sale:Sale, options:{disableWarnings?:boolean} = {}):Promise<void>{
		let result = await this.createInvoiceUbl(vue, sale, options);
		let blob = new Blob([result], {type: "application/xml"});
		downloadBlob(blob, `${sale.ComputedFriendlyID}.xml`);
	}

	public async createArchive(vue:Vue, options:ICreateUBLArchiveOptions = {}):Promise<Job>{
		await this.showWarnings(vue);
		let body = {
			beginDate: options.startDate,
			endDate: options.endDate,
			message: options.message,
			journalIds: options.journalIds || [],
			saleIds: options.saleIds || [],
			typeIds: options.typeIds || []
		};
		let result = await Axios.post(`${this.url}/create-archive`, body);
		return new Job(result.data);
	}

	public async deleteArchive(archive:UblS3UploadedFile):Promise<void>{
		await Axios.delete(`${this.url}/archives/${archive.ID}`);
	}

	public async downloadArchive(file:UblS3UploadedFile):Promise<void>{
		let result = await Axios.get(`${this.url}/archives/${file.ID}/download`);
		let url = result.data.Url;
		downloadUrl(url, file.FileName);
	}

	public async markArchiveSent(archive:UblS3UploadedFile, to:string):Promise<UblS3UploadedFile>{
		let result = await Axios.post(`${this.url}/archives/${archive.ID}/sent`, {to});
		return new UblS3UploadedFile(result.data);
	}

	public async getArchive(id:number):Promise<UblS3UploadedFile>{
		let result = await Axios.get(`${this.url}/archives/${id}`);
		return new UblS3UploadedFile(result.data);
	}

	public async getArchives():Promise<UblS3UploadedFile[]>{
		let result = await Axios.get(`${this.url}/archives`);
		return result.data.map((f:any)=>new UblS3UploadedFile(f));
	}

	public async getArchiveFromPublicUrl(dossierId:number, key:string):Promise<IPublicUblFile>{
		let result = await axios.get(`${this.publicUrl}/public-link/${dossierId}/${key}`);
		return {
			fileInfo: new UblS3UploadedFile(result.data.fileInfo),
			userName: result.data.userName,
			companyName: result.data.companyName
		};
	}

	public async downloadArchiveFromPublicUrl(dossierId:number, file:UblS3UploadedFile){
		let result = await axios.get(`${this.publicUrl}/public-link/${dossierId}/${file.PublicKey}/download`);
		let url = result.data.Url;
		downloadUrl(url, file.FileName);
	}

	public async createPurchaseUbl(vue:Vue, purchase:Purchase){
		await this.showWarnings(vue);
		let result = await Axios.post(`${this.url}/download-purchase-ubl/${purchase.ID}`);
		return result.data;
	}

	public async downloadPurchaseUbl(vue:Vue, purchase:Purchase):Promise<void>{
		let result = await this.createPurchaseUbl(vue, purchase);
		let blob = new Blob([result], {type: "application/xml"});
		downloadBlob(blob, `${purchase.ComputedFriendlyID}.xml`);
	}

	public async createPurchaseArchive(vue:Vue, options:ICreatePurchaseUBLArchiveOptions = {}):Promise<Job>{
		await this.showWarnings(vue);
		let body = {
			beginDate: options.beginDate,
			endDate: options.endDate,
			message: options.message || "",
			vatPeriod: options.vatPeriod || 0,
			journalIds: options.journalIds || [],
			purchaseIds: options.purchaseIds || []
		};
		let result = await Axios.post(`${this.url}/create-purchase-archive`, body);
		return new Job(result.data);
	}

	public async purchaseFromUbl(file:File):Promise<IUblPurchaseWrapper>{
		let data = new FormData();
		data.append("file", file);
		let result = await Axios.post(`${this.url}/purchase-from-ubl`, data);
		return result.data;
	}

}

export const UblService = new UblServiceClass();